import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import "./log_expenses_company.css";
import {
  FaSave,
  FaCalendarAlt,
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes,
  FaReceipt,
  FaTag,
  FaPoundSign,
  FaCheckCircle,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  categoryApi,
  saveExpenseApi,
  takeImageUploadExpenseApi,
} from "../../services/apiCall";

export default function LogExpensesCompanyPage() {
  const [date, setDate] = useState("");
  const [category, setCategory] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [vatTax, setVatTax] = useState("");
  const [notes, setNotes] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [extracted, setExtracted] = useState(false);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);
  const businessName =
    useSelector((state) => state?.user?.businessName) || "Your Business";

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await categoryApi();
        setCategories(res?.data?.expenseCategories ?? []);
      } catch (err) {}
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const gross = parseFloat(grossAmount) || 0;
    const vat = parseFloat(vatTax) || 0;
    const net = gross - vat;
    if (net >= 0) setNetAmount(net.toFixed(2));
  }, [grossAmount, vatTax]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setImage(file);
    setIsExtracting(true);
    setExtracted(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", "expense");

    try {
      const response = await takeImageUploadExpenseApi(formData);
      const data = response?.data?.data;

      if (data) {
        if (data.category) setCategory(data.category.toString());
        if (data.gross) setGrossAmount(data.gross.toString());
        if (data.vat) setVatTax(data.vat.toString());
        if (data.net) setNetAmount(data.net.toString());
        if (data.description) setNotes(data.description);
        if (data.createdAt)
          setDate(new Date(data.createdAt).toISOString().split("T")[0]);
        setExtracted(true);
        toast.success("Receipt scanned — fields auto-filled!");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to extract data from receipt.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!date || !category || !grossAmount) {
      toast.error("Date, Category and Gross Amount are required.");
      return;
    }

    const gross = parseFloat(grossAmount);
    if (isNaN(gross) || gross <= 0) {
      toast.error("Gross amount must be a positive number.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        category,
        description: notes,
        gross: parseFloat(grossAmount),
        vat: parseFloat(vatTax) || 0,
        net: parseFloat(netAmount),
        businessId,
        createdBy: userId,
        transactionDate: date,
      };

      const response = await saveExpenseApi(payload);
      if (response?.data?.expense != null) {
        toast.success("Expense saved successfully!");
        setDate(""); setCategory(""); setGrossAmount("");
        setNetAmount(""); setVatTax(""); setNotes("");
        setImage(null); setImagePreview(null); setExtracted(false);
      }
    } catch (err) {
      console.error("Error saving expense:", err);
      toast.error(err?.message || "Failed to save expense.");
    } finally {
      setIsSaving(false);
    }
  };

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        {/* Header */}
        <header className="dashboard-header">
          <div>
            <h2 className="dashboard-title">Log Expense</h2>
            <p className="dashboard-sub">Create and record a new expense</p>
          </div>
          <div className="dashboard-info">
            <h3 className="dashboard-company">{businessName}</h3>
            <p className="dashboard-date">{today}</p>
          </div>
        </header>

        {/* Card */}
        <section className="expense-card">
          {/* Top strip */}
          <div className="card-header-strip">
            <FaReceipt />
            <h3>Expense Details</h3>
          </div>

          {/* Grid */}
          <div className="expense-grid">
            {/* Date */}
            <div className="form-item">
              <label>Date <span className="required">*</span></label>
              <div className="input-icon-wrap">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>

            {/* Category */}
            <div className="form-item">
              <label>Category <span className="required">*</span></label>
              <div className="input-icon-wrap">
                <FaTag className="input-icon" />
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Gross Amount */}
            <div className="form-item">
              <label>Gross Amount <span className="required">*</span></label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="0.00"
                  value={grossAmount}
                  onChange={(e) => setGrossAmount(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* VAT Tax */}
            <div className="form-item">
              <label>VAT Tax</label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="0.00"
                  value={vatTax}
                  onChange={(e) => setVatTax(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* Net Amount */}
            <div className="form-item">
              <label>Net Amount <span style={{ fontSize: ".75rem", color: "#9ba3c0", fontWeight: 400 }}>(auto)</span></label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="auto"
                  value={netAmount}
                  readOnly
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="form-item full">
              <label>Notes (optional)</label>
              <textarea
                placeholder="Add extra details about this expense…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Upload Receipt */}
            <div className="form-item full">
              <label>Attach Receipt</label>
              <div className="upload-zone">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleImageChange}
                />
                <div className="upload-icon-wrap">
                  {isExtracting ? (
                    <FaSpinner className="spin" style={{ color: "#fff", fontSize: "1.2rem" }} />
                  ) : (
                    <FaCloudUploadAlt />
                  )}
                </div>
                <h4>{image ? image.name : "Click to upload receipt"}</h4>
                <p>Drag & drop or click to browse</p>
                <span className="upload-hint">Supports: JPG, PNG, PDF</span>
                {isExtracting && (
                  <div className="extracting-badge">
                    <FaSpinner className="spin" /> Extracting data…
                  </div>
                )}
                {extracted && !isExtracting && (
                  <div className="extracting-badge" style={{ background: "#dcfce7", color: "#16a34a" }}>
                    <FaCheckCircle /> Data extracted!
                  </div>
                )}
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div className="receipt-preview">
                  <img src={imagePreview} alt="Receipt preview" />
                  <button
                    className="remove-img"
                    onClick={() => { setImage(null); setImagePreview(null); setExtracted(false); }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="footer-save">
            <button className="btn-save" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <><FaSpinner className="spin" /> Saving…</>
              ) : (
                <><FaSave /> Save Expense</>
              )}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
